import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  SOCIAL_PLATFORMS,
  DEFAULT_CHANNEL_TYPES,
  CHANNEL_LIST,
} from './constants';
import { findConnectedInbox } from './channelMatchers';
import { useChannelConfig } from './useChannelConfig';

// Pull the handle/username out of a detected social URL, formatted per channel.
const extractHandle = ({ type, url }) => {
  try {
    const { pathname } = new URL(url);
    const path = pathname.replace(/^\/+|\/+$/g, '');
    if (type === 'whatsapp') {
      const digits = path.replace(/\D/g, '');
      return digits ? `+${digits}` : '';
    }
    if (type === 'line') return path;
    return path.startsWith('@') ? path : `@${path}`;
  } catch {
    return '';
  }
};

// Derives the channel rows for the inbox-setup step from the account's detected
// brand_info socials and the real connected inboxes,
// keeping InboxSetup.vue focused on layout, connect routing, and completion.
export function useDetectedChannels() {
  const { currentAccount } = useAccount();
  const inboxes = useMapGetter('inboxes/getInboxes');
  const { isConfigured } = useChannelConfig();

  const brandSocials = computed(
    () => currentAccount.value?.custom_attributes?.brand_info?.socials || []
  );

  const connectedChannels = computed(() =>
    brandSocials.value
      .filter(social => SOCIAL_PLATFORMS[social.type] && social.url)
      .map(social => ({
        type: social.type,
        handle: extractHandle(social),
        labelKey: SOCIAL_PLATFORMS[social.type].labelKey,
        inbox: { channel_type: SOCIAL_PLATFORMS[social.type].channelType },
      }))
  );

  // The real inbox backing a channel, if one exists — returned (not just a
  // boolean) so the row can show the connected account's real name.
  const connectedInbox = channel =>
    findConnectedInbox(inboxes.value, channel.inbox);

  const detectedChannels = computed(() =>
    connectedChannels.value
      // Hide channels whose installation OAuth credentials are missing — their
      // connect flow would only error.
      .filter(channel => isConfigured(channel.type))
  );

  // Keep the three primary setup paths visible, independent of detected socials
  // or embedded-signup credentials. WhatsApp also supports manual Cloud setup.
  const displayedChannels = computed(() =>
    DEFAULT_CHANNEL_TYPES.map(type => ({
      ...CHANNEL_LIST.find(channel => channel.type === type),
      handle:
        detectedChannels.value.find(channel => channel.type === type)?.handle ||
        '',
    }))
  );

  const remainingChannels = computed(() => {
    // Exclude whatever is already shown as a row (detected or defaulted) so the
    // footer preview doesn't duplicate it.
    const shownTypes = new Set(displayedChannels.value.map(c => c.type));
    return Object.entries(SOCIAL_PLATFORMS)
      .filter(([type]) => !shownTypes.has(type))
      .filter(([type]) => isConfigured(type))
      .slice(0, 3)
      .map(([type, { labelKey, channelType }]) => ({
        type,
        labelKey,
        inbox: { channel_type: channelType },
      }));
  });

  const hasDetectedChannels = computed(() =>
    displayedChannels.value.some(channel => channel.handle)
  );

  return {
    displayedChannels,
    remainingChannels,
    connectedInbox,
    hasDetectedChannels,
  };
}
